import { z } from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

type ProductLineOptions = {
    productLines: ProductLine[] | undefined;
};
export const productLineHierarchyMap = (options: ProductLineOptions) => {
    const { productLines } = options;

    const accMap = new Map<string, string[]>();
    if (productLines === undefined) {
        return accMap;
    }

    // Helper function to get all descendants of a product line
    const getDescendants = (productLineName: string): string[] => {
        const descendants = [productLineName]; // Include the product line itself

        // Find children of this product line
        const children = productLines.filter((pl: ProductLine) => pl.parent?.name === productLineName);

        // Recursively get descendants of children
        for (const child of children) {
            descendants.push(...getDescendants(child.name));
        }

        return descendants;
    };

    // Build the map for all product lines
    for (const productLine of productLines) {
        accMap.set(productLine.name, getDescendants(productLine.name));
    }

    return accMap;
};

type Options = {
    page?: number;
    itemsPerPage?: number;
    initSearch?: string;
};

export const useGetAllProductLines = (options?: Options) => {
    const { page = 1, itemsPerPage = 100, initSearch = "" } = options || {};

    const searchApi = useDelayedSearch({ startSearch: initSearch });
    const query = useQuery({
        queryKey: ["allProductLines", { page, itemsPerPage }, searchApi.delayedSearch],
        queryFn: async () => {
            const url = `/api/product_lines`;
            const response = await loggedInApiCall.get(url, {
                params: {
                    "order[name]": "asc",
                    name: searchApi.delayedSearch,
                    page,
                    itemsPerPage,
                },
            });
            return response;
        },
        select: (request) => {
            const productLine = request.data["hydra:member"] ?? [];
            const data = zodParseWithLog(productLine, ProductLineZodSchema.array(), "allProductLines");
            return data;
        },
    });
    const isReady = isQueryReady(query, true);

    return {
        productLines: query.data,
        productLinesHierarchy: productLineHierarchyMap({ productLines: query.data }),
        isReady,
        searchApi,
        ...query,
    };
};

export type ProductLine = {
    "@id": IRI;
    id: number;
    name: string;
    parent?: {
        "@id": IRI;
        id: number;
        name: string;
    } | null;
    products?: Product[];
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
};

export type Product = {
    "@id": string;
    id: number;
};

export const ProductSchema: z.ZodSchema<Product> = z.object({
    "@id": z.string(),
    id: z.number(),
});

export const ProductLineZodSchema: z.ZodSchema<ProductLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    parent: z
        .object({
            "@id": z.string(),
            id: z.number(),
            name: z.string(),
        })
        .optional()
        .nullable(),
    products: ProductSchema.array().optional(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
});

export type ProductLineForm = {
    id?: number;
    name: string;
    parent?: {
        "@id": IRI;
        id: number;
        name: string;
    } | null;
    products?: Product[];
    canView?: boolean;
    canModify?: boolean;
    canDelete?: boolean;
};
